"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { preserveProductAnalyticsTrafficMarker } from "@/lib/analytics-traffic";
import { captureMarketingEvent } from "@/lib/marketing-analytics-client";
import type { MarketingEventAudience } from "@/lib/marketing-analytics-policy";
import { preserveCampaignParams as carryCampaignParams } from "@/lib/comparison-links";

type MarketingEventPropertyValue = string | number | boolean | null | undefined;
type AdditionalMarketingEvent = {
  eventName: string;
  eventProperties?: Record<string, MarketingEventPropertyValue>;
  analyticsAudience?: MarketingEventAudience;
};

type MarketingTrackedLinkProps = React.ComponentProps<typeof Link> & {
  eventName: string;
  eventProperties?: Record<string, MarketingEventPropertyValue>;
  analyticsAudience?: MarketingEventAudience;
  additionalEvents?: readonly AdditionalMarketingEvent[];
  preserveCampaignParams?: boolean;
};

export const MarketingTrackedLink = React.forwardRef<
  HTMLAnchorElement,
  MarketingTrackedLinkProps
>(function MarketingTrackedLink(
  { eventName, eventProperties = {}, analyticsAudience = "all", additionalEvents = [], preserveCampaignParams = false, href, onClick, ...props },
  ref,
) {
  const pathname = usePathname();
  const [campaignUrl, setCampaignUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (preserveCampaignParams) setCampaignUrl(window.location.href);
  }, [href, pathname, preserveCampaignParams]);
  // Give Next's router the resolved href too; changing the DOM anchor alone
  // would lose these parameters on a client-side internal navigation.
  const resolvedHref = preserveCampaignParams && typeof href === "string" && campaignUrl
    ? carryCampaignParams(href, campaignUrl)
    : href;

  return (
    <Link
      {...props}
      href={resolvedHref}
      ref={ref}
      onClick={(event) => {
        onClick?.(event);

        if (!event.defaultPrevented) {
          if (typeof resolvedHref === "string") {
            event.currentTarget.href = preserveProductAnalyticsTrafficMarker(
              resolvedHref,
              window.location.href,
            );
          }

          for (const additionalEvent of additionalEvents) {
            captureMarketingEvent(
              additionalEvent.eventName,
              additionalEvent.eventProperties,
              { analyticsAudience: additionalEvent.analyticsAudience },
            );
          }

          captureMarketingEvent(eventName, eventProperties, {
            analyticsAudience,
          });
        }
      }}
    />
  );
});
